import {
  type MatrixClient,
  SetPresence,
  type User,
  UserEvent,
} from 'matrix-js-sdk'
import type { IStatusResponse } from 'matrix-js-sdk/lib/@types/requests'
import {
  getMatrixClient,
  startMatrixClient,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { formatRelativeTime } from '@/utils/formatters'

interface PresenceState {
  online: boolean
  lastSeenAt: number | null
}

type PresenceListener = (mxid: string, online: boolean) => void

const presenceCache = new Map<string, PresenceState>()
const presenceForbiddenMxids = new Set<string>()
const listeners = new Set<PresenceListener>()
let presenceStarted = false

function isOnlineFromUser(user: User): boolean {
  if (user.currentlyActive) {
    return true
  }

  return user.presence === 'online'
}

function isOnlineFromStatus(status: IStatusResponse): boolean {
  if (status.currently_active) {
    return true
  }

  return status.presence === 'online'
}

function presenceFromUser(user: User): PresenceState {
  const online = isOnlineFromUser(user)
  const lastSeenAt = user.lastPresenceTs > 0 ? user.lastPresenceTs : null

  return {
    online,
    lastSeenAt: online ? null : lastSeenAt,
  }
}

function presenceFromStatus(status: IStatusResponse): PresenceState {
  const online = isOnlineFromStatus(status)
  let lastSeenAt: number | null = null

  if (!online && typeof status.last_active_ago === 'number' && status.last_active_ago >= 0) {
    lastSeenAt = Date.now() - status.last_active_ago
  }

  return { online, lastSeenAt }
}

function isPresenceUnavailableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const matrixError = error as { httpStatus?: number; errcode?: string; statusCode?: number }
  const status = matrixError.httpStatus ?? matrixError.statusCode
  // 403 — presence disabled / forbidden; 5xx — Synapse/proxy hiccups (seen as 502 on alpha)
  return status === 403
    || status === 502
    || status === 503
    || status === 504
    || matrixError.errcode === 'M_FORBIDDEN'
}

function statesEqual(a: PresenceState | undefined, b: PresenceState): boolean {
  return a?.online === b.online && a?.lastSeenAt === b.lastSeenAt
}

function notifyPresence(mxid: string, state: PresenceState) {
  const prev = presenceCache.get(mxid)
  if (statesEqual(prev, state)) {
    return
  }

  presenceCache.set(mxid, state)
  listeners.forEach(listener => listener(mxid, state.online))
}

async function resolvePresence(matrixClient: MatrixClient, mxid: string): Promise<PresenceState | undefined> {
  if (presenceForbiddenMxids.has(mxid)) {
    return undefined
  }

  const cachedUser = matrixClient.getUser(mxid)
  if (cachedUser?.lastPresenceTs) {
    return presenceFromUser(cachedUser)
  }

  try {
    const status = await matrixClient.getPresence(mxid)
    return presenceFromStatus(status)
  } catch (error) {
    if (isPresenceUnavailableError(error)) {
      presenceForbiddenMxids.add(mxid)
    }

    return undefined
  }
}

function onUserPresence(_event: unknown, user: User) {
  notifyPresence(user.userId, presenceFromUser(user))
}

async function ensurePresenceSync(matrixClient: MatrixClient): Promise<void> {
  if (presenceStarted) {
    return
  }

  presenceStarted = true
  await matrixClient.setSyncPresence(SetPresence.Online)
  matrixClient.on(UserEvent.Presence, onUserPresence)
  matrixClient.on(UserEvent.CurrentlyActive, onUserPresence)
  matrixClient.on(UserEvent.LastPresenceTs, onUserPresence)
}

export function getCachedMatrixPresence(mxid: string): boolean | undefined {
  return presenceCache.get(mxid)?.online
}

export function getCachedMatrixLastSeenAt(mxid: string): number | null {
  return presenceCache.get(mxid)?.lastSeenAt ?? null
}

/** Подпись «был(а) …» по timestamp последнего присутствия */
export function formatMatrixLastSeen(lastSeenAt: number | null | undefined, now: number = Date.now()): string {
  if (!lastSeenAt || lastSeenAt <= 0) {
    return ''
  }

  const relative = formatRelativeTime(new Date(lastSeenAt), now)
  if (!relative) {
    return ''
  }

  return `был(а) ${relative.charAt(0).toLowerCase()}${relative.slice(1)}`
}

export async function trackMatrixPresence(mxids: string[]): Promise<void> {
  const uniqueMxids = [...new Set(mxids.filter(mxid => mxid.startsWith('@')))]
  if (!uniqueMxids.length) {
    return
  }

  const matrixClient = await startMatrixClient()
  await ensurePresenceSync(matrixClient)

  await Promise.all(
    uniqueMxids.map(async mxid => {
      const state = await resolvePresence(matrixClient, mxid)
      if (state) {
        presenceCache.set(mxid, state)
      }
    }),
  )
}

export function subscribeMatrixPresence(onUpdate: PresenceListener): () => void {
  listeners.add(onUpdate)

  void startMatrixClient().then(async matrixClient => {
    await ensurePresenceSync(matrixClient)
  })

  return () => {
    listeners.delete(onUpdate)
  }
}

export function resetMatrixPresenceState(): void {
  presenceCache.clear()
  presenceForbiddenMxids.clear()
  presenceStarted = false

  try {
    const matrixClient = getMatrixClient()
    matrixClient.removeListener(UserEvent.Presence, onUserPresence)
    matrixClient.removeListener(UserEvent.CurrentlyActive, onUserPresence)
    matrixClient.removeListener(UserEvent.LastPresenceTs, onUserPresence)
  } catch {
    // Клиент ещё не создан
  }
}
